/**
 * Registers the service worker and makes an update actually arrive.
 *
 * A phone keeps the app it last downloaded, and an installed PWA can go weeks
 * without a real reload. The worker is built to take over the moment a new
 * version is ready (skipWaiting and clientsClaim, from registerType
 * autoUpdate), but taking over does nothing for the page that is already
 * open: it keeps running the old script until somebody closes it. So when a
 * worker takes control of a page that was already controlled, the page
 * reloads onto the new version. A first install does not reload (nothing was
 * stale).
 *
 * The guest app shipped without this (2026-09-26 bug report: stale "OK"
 * badge text, a three-column tile grid a fix had already removed, a React
 * crash reading "insertBefore" on an already-open tab). Every one of those
 * is the signature of a page still running yesterday's JavaScript against
 * today's index.html after several same-day deploys. Ops already had this
 * file; the guest app did not.
 *
 * It also asks for a new version whenever the app comes back to the front
 * and every half hour, so a phone that sits open all day still catches up.
 *
 * This replaces the bare script tag the PWA plugin injects (injectRegister
 * is off in vite.config.ts), which registered the worker and did nothing
 * else.
 */

export const UPDATE_CHECK_EVERY_MS = 30 * 60 * 1000;

type Nav = Pick<Navigator, 'serviceWorker'>;

export function registerServiceWorker(nav: Nav = navigator, win: Window = window): void {
  const container = nav.serviceWorker;
  if (!container) return;

  // Whether this page has had a worker in charge of it yet. The first
  // install claims the page too, and that is not an update: nothing on
  // screen is stale.
  let controlled = Boolean(container.controller);
  let reloading = false;
  container.addEventListener('controllerchange', () => {
    if (!controlled) {
      controlled = true;
      return;
    }
    if (reloading) return;
    reloading = true;
    win.location.reload();
  });

  const start = () => {
    void container
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        const check = () => void registration.update().catch(() => undefined);
        win.setInterval(check, UPDATE_CHECK_EVERY_MS);
        win.document.addEventListener('visibilitychange', () => {
          if (win.document.visibilityState === 'visible') check();
        });
      })
      .catch(() => undefined);
  };

  if (win.document.readyState === 'complete') start();
  else win.addEventListener('load', start, { once: true });
}
